/** @jsxImportSource @emotion/react */
import type { DraggableSyntheticListeners, UniqueIdentifier } from '@dnd-kit/core'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  createContext,
  useContext,
  useMemo,
  type CSSProperties,
  type PropsWithChildren,
} from 'react'

import { Icon } from '../../Icon'
import { draggableAccordionIcon } from './SortableItem.styles'

interface Props {
  id: UniqueIdentifier
}

interface Context {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  attributes: Record<string, any>
  listeners: DraggableSyntheticListeners
  ref(node: HTMLElement | null): void
}

const SortableItemContext = createContext<Context>({
  attributes: {},
  listeners: undefined,

  ref() {},
})

export function SortableItem({ children, id }: PropsWithChildren<Props>) {
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
  } = useSortable({ id })

  const context = useMemo(
    () => ({
      attributes,
      listeners,
      ref: setActivatorNodeRef,
    }),
    [attributes, listeners, setActivatorNodeRef],
  )
  const style: CSSProperties = {
    opacity: isDragging ? 0.4 : undefined,
    transform: CSS.Translate.toString(transform),
    transition,
  }

  return (
    <SortableItemContext.Provider value={context}>
      <Box ref={setNodeRef} style={style}>
        {children}
      </Box>
    </SortableItemContext.Provider>
  )
}

export function DragHandle() {
  const { attributes, listeners, ref } = useContext(SortableItemContext)
  const theme = useTheme()

  return (
    <Box css={draggableAccordionIcon({ theme })} {...attributes} {...listeners} ref={ref}>
      <Icon iconName="Drag" size="medium" colour={theme.palette.primary.main} />
    </Box>
  )
}
